import { Button } from '@/components/ui/button';
import { FileText, Download, Shield, Zap, FileCode, Video, File } from 'lucide-react';

interface AlgorithmFile {
    id: string;
    name: string;
    type: 'source' | 'executable' | 'documentation' | 'video' | 'other';
    file: {
        url: string;
        filename: string;
        filesize: number;
    };
}

interface Algorithm {
    id: string;
    files: AlgorithmFile[];
}

interface AlgorithmFilesProps {
    algorithm: Algorithm;
}

export function AlgorithmFiles({ algorithm }: AlgorithmFilesProps) {
    const getFileIcon = (type: string) => {
        switch (type) {
            case 'source': return <FileCode className="h-5 w-5 text-blue-500" />;
            case 'executable': return <Zap className="h-5 w-5 text-yellow-500" />;
            case 'documentation': return <FileText className="h-5 w-5 text-green-500" />;
            case 'video': return <Video className="h-5 w-5 text-purple-500" />;
            default: return <File className="h-5 w-5 text-gray-500" />;
        }
    };

    const formatSize = (bytes: number) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    if (!algorithm.files || algorithm.files.length === 0) {
        return (
            <div className="text-center py-12 bg-card rounded-lg border border-dashed">
                <File className="h-12 w-12 mx-auto text-muted-foreground mb-4 opacity-20" />
                <p className="text-muted-foreground">No files available for this algorithm</p>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between mb-2">
                <h2 className="text-2xl font-semibold">Algorithm Files</h2>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Shield className="h-4 w-4" />
                    Securely stored on Cloudflare R2
                </div>
            </div>

            <div className="grid gap-4">
                {algorithm.files.map((file) => (
                    <div key={file.id} className="flex items-center justify-between p-4 bg-card rounded-lg border hover:border-primary/50 transition-colors group">
                        <div className="flex items-center gap-4">
                            <div className="p-2 bg-muted rounded-md group-hover:bg-primary/10 transition-colors">
                                {getFileIcon(file.type)}
                            </div>
                            <div>
                                <div className="font-medium text-foreground">{file.name}</div>
                                <div className="flex items-center gap-3 text-xs text-muted-foreground uppercase">
                                    <span>{file.type}</span>
                                    <span>•</span>
                                    <span>{formatSize(file.file.filesize)}</span>
                                </div>
                            </div>
                        </div>

                        <Button variant="ghost" size="sm" asChild>
                            <a href={file.file.url} download={file.file.filename}>
                                <Download className="h-4 w-4 mr-2" />
                                Download
                            </a>
                        </Button>
                    </div>
                ))}
            </div>
        </div>
    );
}
